import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ClassNameMap } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import { Chip, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import { Controller, useFormContext } from 'react-hook-form';
import { InputLabelProps } from '@mui/material/InputLabel';
import { GroupSearchResult } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { mapToUiAccessSope } from './utils';
import { useMoreMessage } from './hooks/useMoreMessage';
import { SelectHierarchyOptionItem } from './SelectHierarchyOption';

const defaultAutoCompleteWidth = 28;
const newDefaultAutoCompleteWith = 27;
type makeStylesProps = {
  classes: ClassNameMap | undefined;
  isTiAutoAcceptUserSettingsEnabled: boolean;
};
const useStyles = makeStyles((theme) => ({
  autoComplete: {
    minWidth: ({ isTiAutoAcceptUserSettingsEnabled }: makeStylesProps) =>
      isTiAutoAcceptUserSettingsEnabled
        ? theme.spacing(newDefaultAutoCompleteWith + 2)
        : theme.spacing(defaultAutoCompleteWidth + 2),

    margin: theme.spacing(1),
    paddingRight: theme.spacing(0),
    '&.MuiAutocomplete-hasPopupIcon.css-gtwism-MuiAutocomplete-root .MuiOutlinedInput-root': {
      paddingRight: theme.spacing(2.5)
    },
    '& .MuiOutlinedInput-root': {
      paddingRight: ({ isTiAutoAcceptUserSettingsEnabled }: makeStylesProps) =>
        isTiAutoAcceptUserSettingsEnabled
          ? `${theme.spacing(3)}!important`
          : theme.spacing(8)
    },
    '& .MuiAutocomplete-tag': {
      margin: ({ isTiAutoAcceptUserSettingsEnabled }: makeStylesProps) =>
        isTiAutoAcceptUserSettingsEnabled
          ? theme.spacing(0.25, 0.25, 0.25, 1)
          : theme.spacing(0.25, 0.25, 0.25, 0.25)
    }
  },
  chip: {
    minWidth: theme.spacing(2),
    maxWidth: ({ isTiAutoAcceptUserSettingsEnabled }: makeStylesProps) =>
      isTiAutoAcceptUserSettingsEnabled ? theme.spacing(16) : theme.spacing(23),
    marginLeft: theme.spacing(0.4)
  }
}));

const useInputLabelStyles = makeStyles(() => ({
  root: {
    maxWidth: 'calc(100% - 35px)'
  },
  focused: {
    maxWidth: 'calc(133% - 40px)'
  }
}));

type GroupSelectProp = {
  groupType: string;
  handleSearch: (groupType: string, queryText: string | null) => void;
  onOpen: () => void;
  options: GroupSearchResult[];
  hasMore: boolean;
  loading: boolean;
  rowID: string;
  defaultValues: any[];
  classes?: ClassNameMap;
};

export const GroupSelect: React.FC<GroupSelectProp> = ({
  groupType,
  handleSearch,
  onOpen,
  options,
  hasMore,
  loading,
  rowID,
  defaultValues,
  classes: overrideClasses
}: GroupSelectProp) => {
  const [search, setSearch] = useState('');
  const searching = useRef(false);
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const classes = useStyles({
    classes: overrideClasses,
    isTiAutoAcceptUserSettingsEnabled
  });
  const inputLabelClasses = useInputLabelStyles();
  const message = useMoreMessage(groupType);
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const [autoCompleteWidth, setAutoCompleteWidth] = useState(
    isTiAutoAcceptUserSettingsEnabled
      ? newDefaultAutoCompleteWith
      : defaultAutoCompleteWidth
  );
  const { formatMessage } = useIntl();
  const { control } = useFormContext();
  const groupSelectID = useMemo(() => `${rowID}.${groupType}`, [
    groupType,
    rowID
  ]);

  const handleInputChange = useCallback((event, value) => {
    searching.current = true;
    setSearch(value);
  }, []);

  useDebounce(
    () => {
      if (searching.current) handleSearch(groupType, search);
    },
    500,
    [search]
  );

  const renderOption = useCallback((props, option, { selected }) => {
    return (
      <li {...props} key={props.id}>
        <SelectHierarchyOptionItem
          hierarchyLevel={option.level}
          title={option.displayText}
          selected={selected}
        />
      </li>
    );
  }, []);
  const filterOptions = useCallback(
    (options) => {
      hasMore && options.push(message);
      return options as GroupSearchResult[];
    },
    [hasMore, message]
  );

  const getOptionSelected = useCallback((option, value) => {
    return option.id === value.id;
  }, []);

  const loadingText = useMemo(() => {
    return (
      <Typography data-testid="auto-complete-loading">
        {formatMessage({ id: 'settings.loading' })}
      </Typography>
    );
  }, [formatMessage]);

  const label = useMemo(() => {
    return formatMessage({ id: `settings.groups.single.${groupType}` });
  }, [formatMessage, groupType]);

  const floatingLabelText = useMemo(() => {
    return label.length > autoCompleteWidth - 3
      ? label.substring(0, autoCompleteWidth - 3) + '...'
      : label;
  }, [autoCompleteWidth, label]);

  const noOptionText = useMemo(() => {
    return formatMessage({ id: `settings.groups.no.${groupType}` });
  }, [formatMessage, groupType]);

  const setAutoCompleteFieldWidth = useCallback((data) => {
    autoCompleteRef.current && data.length > 1
      ? setAutoCompleteWidth(
          autoCompleteRef.current.getBoundingClientRect().width / 8
        )
      : setAutoCompleteWidth(defaultAutoCompleteWidth);
  }, []);

  const toolTipTitle = useMemo(
    () => (floatingLabelText && floatingLabelText.endsWith('...') ? label : ''),
    [floatingLabelText, label]
  );

  const inputProps = useCallback(
    (params) => ({
      ...params.InputProps,
      label: floatingLabelText
    }),
    [floatingLabelText]
  );

  const handleRenderTags = useCallback(
    (tagValue, getTagProps) => {
      return tagValue.map((option: GroupSearchResult, index: number) => {
        return (
          <Tooltip key={option.id} title={option.displayText} placement="top">
            <Chip
              {...getTagProps({ index })}
              label={option.displayText}
              className={classes.chip}
              size="small"
            />
          </Tooltip>
        );
      });
    },
    [classes.chip]
  );
  const handleOnBlur = useCallback(() => {
    setAutoCompleteWidth(defaultAutoCompleteWidth);
  }, []);

  const onChangeHandler = useCallback(
    (onChange) => (
      event: React.ChangeEvent<any>,
      data: GroupSearchResult[]
    ) => {
      setAutoCompleteFieldWidth(data);

      const newData = mapToUiAccessSope(data);
      onChange(newData);
    },
    [setAutoCompleteFieldWidth]
  );

  const inputLabelProps = useMemo<InputLabelProps>(
    () => ({
      classes: inputLabelClasses
    }),
    [inputLabelClasses]
  );

  const getInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <Tooltip title={toolTipTitle}>
        <TextField
          {...params}
          InputProps={inputProps(params)}
          variant="outlined"
          label={floatingLabelText}
          placeholder=""
          data-testid={`simple-Search-${groupSelectID}`.toLowerCase()}
          InputLabelProps={inputLabelProps}
        />
      </Tooltip>
    ),
    [
      toolTipTitle,
      inputProps,
      floatingLabelText,
      groupSelectID,
      inputLabelProps
    ]
  );

  return (
    <Controller
      render={({ field: { onChange, ...props } }) => (
        <Autocomplete
          multiple
          data-testid={`setting-autocomplete-${groupType}`.toLowerCase()}
          loading={loading}
          loadingText={loadingText}
          renderOption={renderOption}
          isOptionEqualToValue={getOptionSelected}
          inputValue={search}
          limitTags={1}
          onOpen={onOpen}
          filterOptions={filterOptions}
          onInputChange={handleInputChange}
          size="small"
          options={options}
          disableCloseOnSelect
          getOptionLabel={(option) => option.displayText}
          className={classes.autoComplete}
          noOptionsText={noOptionText}
          renderTags={handleRenderTags}
          renderInput={getInputComponent}
          onChange={onChangeHandler(onChange)}
          {...props}
          ref={autoCompleteRef}
          onBlur={handleOnBlur}
        />
      )}
      name={groupSelectID}
      control={control}
      defaultValue={defaultValues}
    />
  );
};
